import React from "react";
import { Pencil, SquareKanban, Trash2 } from "lucide-react";
import DataTable, { createActionsColumn } from "../data-table";
import PriorityBadge from "./PriorityBadge";
import StatusBadge from "./StatusBadge";
import ProjectProgressBar from "./ProjectProgressBar";

const formatDate = (dateValue) => {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(date);
};

const getProgressData = (project) => {
  const completed = Number(project?.totalRecruitsCount) || 0;
  const total = Number(project?.totalTasks) || 0;
  const percentage = Number.isFinite(Number(project?.percentage))
    ? Math.max(0, Math.min(100, Number(project.percentage)))
    : total > 0
      ? Math.round((completed / total) * 100)
      : 0;

  return { completed, total, percentage };
};

const PRIORITY_ORDER = { Critical: 4, High: 3, Medium: 2, Moderate: 2, Low: 1 };

function ProjectTable({
  projects,
  onEditProject,
  onOpenProjectBoard,
  onDeleteProject,
  canDelete,
  loading = false,
  filterContent,
  activeFilterCount,
  toolbarRight,
}) {
  const columns = [
    {
      key: "projectName",
      label: "Project Name",
      sortable: true,
      searchable: true
    },
    {
      key: "projectType",
      label: "Project Type",
      sortable: true,
      searchable: true,
      render: (value) => (
        <span className="inline-flex items-center rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
          {value}
        </span>
      ),
    },
    { key: "status", label: "Status", sortable: true, render: (value) => <StatusBadge status={value} /> },
    {
      key: "progress",
      label: "Progress",
      sortable: true,
      width: "190px",
      accessor: getProgressData,
      sortValue: (project) => getProgressData(project).percentage,
      render: ({ completed, total, percentage }) => (
        <div>
          <div className="mb-1 flex items-center justify-between text-[11px] font-semibold text-gray-600 dark:text-app-secondary">
            <span>{`${completed || 0}/${total || 0}`}</span>
            <span>{`${percentage}%`}</span>
          </div>
          <ProjectProgressBar percentage={percentage} />
        </div>
      ),
    },
    {
      key: "priority",
      label: "Priority",
      sortable: true,
      sortValue: (project) => PRIORITY_ORDER[project.priority] ?? null,
      render: (value) => <PriorityBadge priority={value} />,
    },
    { key: "startDate", label: "Start Date", sortable: true, render: formatDate },
    { key: "dueDate", label: "Due Date", sortable: true, render: formatDate },
    {
      key: "assignedUsers",
      label: "Assigned Users",
      searchable: true,
      render: (users = []) => (
        <div className="max-w-[240px] space-y-1">
          {users.slice(0, 3).map((user) => (
            <div key={user} className="truncate text-xs font-medium text-app-secondary" title={user}>
              {user}
            </div>
          ))}
          {users.length > 3 && (
            <div className="text-[11px] font-semibold text-app-muted" title={users.slice(3).join(", ")}>
              +{users.length - 3} more
            </div>
          )}
          {users.length === 0 && <span className="text-gray-400">—</span>}
        </div>
      ),
    },
    createActionsColumn(
      (project) => [
        { label: "Open project board", icon: SquareKanban, onSelect: () => onOpenProjectBoard(project) },
        { label: "Edit", icon: Pencil, onSelect: () => onEditProject(project) },
        {
          label: "Delete",
          icon: Trash2,
          tone: "danger",
          hidden: !canDelete,
          onSelect: () => onDeleteProject(project),
        },
      ],
      { getRowLabel: (project) => project.projectName },
    ),
  ];

  return (
    <DataTable
      ariaLabel="Projects"
      columns={columns}
      data={projects}
      getRowId={(project, index) => project.projectId || project.backendId || index}
      loading={loading}
      loadingRowCount={8}
      searchable
      searchPlaceholder="Search project, type or assignee"
      filterable={Boolean(filterContent)}
      filterContent={filterContent}
      activeFilterCount={activeFilterCount}
      toolbarRight={toolbarRight}
      pagination
      defaultPageSize={10}
      labels={{ empty: "No data found." }}
    />
  );
}

export default ProjectTable;
